import { Fragment, useState } from 'react';
import Loading from '../../../component/common/loading/Loading';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';
import { useSubIcon } from '../../../hooks/useSubIcon';
import * as Icons from '../../../images/icons/icons_component';
import { applyFlagCascade, applySystemAll, countSystem, getAuth, grantState } from './roleMenuTreeUtil';

import '../../../css/data-grid.css';
import '../../../css/role-mng.css';

const HEADER_IDS = {
    view: 'role-tree-hdr-view',
    upyn: 'role-tree-hdr-upyn',
    dlyn: 'role-tree-hdr-dlyn',
};

/**
 * 롤별 메뉴 접근권한 트리 (시스템 > 상위 메뉴 > 하위 메뉴)
 * - 조회 · 등록·수정 · 삭제 체크박스, 상위 메뉴 체크는 하위 메뉴에 일괄 반영(등록·수정은 한 칸, 저장할 때 REYN 과 UPYN 에 같은 값)
 * - 등록·수정 / 삭제를 체크하면 조회도 켜지고, 조회를 풀면 모두 풀린다
 * - 사용 안 하는 메뉴는 (미사용) 으로 연한 붉은 배경에 표시하고, 미사용 상위 메뉴는 처음에 접어 둔다(권한은 그대로 고를 수 있다)
 * - showUnused 가 false 면 미사용 메뉴를 그리지 않고, 일괄 체크 · 전체 해제도 그 메뉴는 건드리지 않는다
 * @param systems buildMenuTree() 결과 systems
 * @param authMap 현재 편집 중 권한 맵
 * @param setAuthMap 권한 맵 setter
 * @param loading 트리 로딩 여부
 * @param selected 롤 선택 여부
 * @param showUnused 미사용 메뉴 표시 여부(개발자 롤 110 이고 '미사용 메뉴 제외' 를 체크하지 않았을 때)
 */
const RoleMenuTree = ({ systems, authMap, setAuthMap, loading, selected, showUnused = true }) => {

    // 접힘 상태 (key → true/false). 정하지 않은 노드는 기본값(미사용 상위 메뉴만 접힘)을 따른다
    const [collapsed, setCollapsed] = useState({});
    const isCollapsedOf = (key, defaultCollapsed = false) => collapsed[key] ?? defaultCollapsed;
    const toggleCollapse = (key, defaultCollapsed = false) =>
        setCollapsed(prev => ({ ...prev, [key]: !(prev[key] ?? defaultCollapsed) }));

    const onFlag = (node, field, checked) => setAuthMap(prev => applyFlagCascade(prev, node, field, checked, showUnused));
    const onSystemAll = (system, checked) => setAuthMap(prev => applySystemAll(prev, system, checked, showUnused));

    // Dynamic Style Hook
    const { lightBackColor, borderColorGray, hoverBackColor } = useDynamicStyle();
    const { NullIcon, EmptyIcon } = useSubIcon();

    const renderCheckbox = (node, field, auth, nameId) => (
        <input
            type='checkbox'
            id={`role-menu-${field}-${node.key}`}
            aria-labelledby={`${nameId} ${HEADER_IDS[field]}`}
            checked={auth[field] === 'Y'}
            onChange={(e) => onFlag(node, field, e.target.checked)}
        />
    );

    const renderNode = (node, depth) => {
        if (!showUnused && node.unused) return null;
        const auth = getAuth(authMap, node.key);
        const children = showUnused ? node.children : node.children.filter(child => !child.unused);
        const hasChildren = children.length > 0;
        const defaultCollapsed = depth === 1 && node.unused;
        const isCollapsed = isCollapsedOf(node.key, defaultCollapsed);
        const nameId = `role-menu-name-${node.key}`;
        const childGranted = children.filter(child => getAuth(authMap, child.key).view === 'Y').length;

        return (
            <Fragment key={node.key}>
                <tr className={`role_tree_depth${depth} ${node.unused ? 'role_tree_unused' : hoverBackColor}`}>
                    <td className='role_tree_name'>
                        {hasChildren ?
                            <button type='button' className='role_tree_expander' aria-expanded={!isCollapsed}
                                aria-label={`${node.menuname} 하위 메뉴`} onClick={() => toggleCollapse(node.key, defaultCollapsed)}>
                                {isCollapsed ? <Icons.ExpandIco /> : <Icons.CollapseIco />}
                            </button>
                            : <span className='role_tree_expander_placeholder' aria-hidden='true' />
                        }
                        <span id={nameId}>{node.menuname}</span>
                        {node.unused &&
                            <span className='role_tree_unused_label' title={node.unusedByChildren ? '하위 메뉴가 모두 미사용입니다' : undefined}>(미사용)</span>}
                        {node.orphan && <span className='role_tree_orphan'>(상위 메뉴 없음)</span>}
                        {hasChildren && <span className='role_tree_count'>({childGranted}/{children.length})</span>}
                    </td>
                    <td>{renderCheckbox(node, 'view', auth, nameId)}</td>
                    <td>{renderCheckbox(node, 'upyn', auth, nameId)}</td>
                    <td>{renderCheckbox(node, 'dlyn', auth, nameId)}</td>
                    <td>{auth.view === 'Y' ? node.modid : ''}</td>
                    <td>{auth.view === 'Y' ? node.moddt : ''}</td>
                </tr>
                {hasChildren && !isCollapsed && children.map(child => renderNode(child, depth + 1))}
            </Fragment>
        );
    };

    const renderSystem = (system) => {
        const systemKey = `sys_${system.sytmidno}`;
        const isCollapsed = isCollapsedOf(systemKey);
        const { granted, total, unusedGranted, unusedTotal } = countSystem(authMap, system);
        // 전체 선택은 사용 메뉴만 고르고, 전체 해제는 보이는 메뉴(미사용을 보일 때는 미사용까지)를 푼다
        const allUsedGranted = grantState(authMap, system.menus, false) === 'all';
        const noneGranted = grantState(authMap, system.menus, showUnused) === 'none';

        return (
            <Fragment key={systemKey}>
                <tr className={`role_tree_system ${lightBackColor}`}>
                    <td className='role_tree_name'>
                        <button type='button' className='role_tree_expander' aria-expanded={!isCollapsed}
                            aria-label={`${system.sytmname} 메뉴`} onClick={() => toggleCollapse(systemKey)}>
                            {isCollapsed ? <Icons.ExpandIco /> : <Icons.CollapseIco />}
                        </button>
                        <span>{system.sytmname}</span>
                        <span className='role_tree_count'>
                            (부여 {granted} / 전체 {total}{showUnused && unusedTotal > 0 && ` · 미사용 부여 ${unusedGranted} / ${unusedTotal}`})
                        </span>
                    </td>
                    <td colSpan='3'>
                        <div className='flex gap-1 justify-center'>
                            <button type='button' className='role_tree_sys_btn' onClick={() => onSystemAll(system, true)} disabled={allUsedGranted}
                                aria-label={`${system.sytmname} 사용 메뉴 전체 선택`} title='사용 메뉴만 선택합니다'>전체 선택</button>
                            <button type='button' className='role_tree_sys_btn' onClick={() => onSystemAll(system, false)} disabled={noneGranted}
                                aria-label={`${system.sytmname} 메뉴 전체 해제`}>전체 해제</button>
                        </div>
                    </td>
                    <td colSpan='2'></td>
                </tr>
                {!isCollapsed && system.menus.map(menu => renderNode(menu, 1))}
            </Fragment>
        );
    };

    return (
        <div className='data_grid_container role_tree'>
            {loading ? <Loading /> :
                <div className='flex flex-col w-full h-full overflow-hidden'>
                    <div className='data_responsive'>
                        <hr className={`border-t-2 mr-1-1 ${borderColorGray}`} />
                        <div className='data_list_table'>
                            <table>
                                <colgroup>
                                    <col width='44%' />
                                    <col width='10%' />
                                    <col width='9%' />
                                    <col width='9%' />
                                    <col width='14%' />
                                    <col width='14%' />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className='border-b-1'>메뉴명</th>
                                        <th className='border-b-1' id={HEADER_IDS.view}>조회</th>
                                        <th className='border-b-1' id={HEADER_IDS.upyn}>등록·수정</th>
                                        <th className='border-b-1' id={HEADER_IDS.dlyn}>삭제</th>
                                        <th className='border-b-1'>수정자</th>
                                        <th className='border-b-1'>수정일</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!selected ? (
                                        <tr>
                                            <td colSpan={6} className='empty_box'>
                                                <span className={`data_empty ${lightBackColor}`}>
                                                    {NullIcon}
                                                    상단 롤 목록에서 롤을 선택하면 메뉴 접근권한이 표시됩니다.
                                                </span>
                                            </td>
                                        </tr>
                                    ) : systems.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className='empty_box'>
                                                <span className={`data_empty ${lightBackColor}`}>
                                                    {EmptyIcon}
                                                    표시할 메뉴가 없습니다.
                                                </span>
                                            </td>
                                        </tr>
                                    ) : (
                                        systems.map(renderSystem)
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <hr className='mr-1-1 co_border_gray' />
                    </div>
                </div>
            }
        </div>
    );
};

export default RoleMenuTree;
